import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import { formatCurrency, formatDate } from '@/lib/utils'
import type { Invoice } from '@/types/invoice'
import type { Devis, LigneDevis } from '@/types/devis'
import { qontoApiCall, downloadQontoAttachment } from './qontoApi'

// Company info (can be moved to config)
const COMPANY_INFO = {
  name: 'LesSablesWeb',
  siret: 'XXX XXX XXX XXXXX',
  address: '123 Rue du Web',
  city: '85100 Les Sables d\'Olonne',
  email: 'contact@lessablesweb.fr',
  phone: '02 XX XX XX XX',
  tva: 'FR XX XXX XXX XXX',
}

// Le PDF de facture est désormais celui généré par Qonto. Plus de génération locale.
async function fetchQontoInvoicePDF(invoice: Invoice): Promise<Blob> {
  if (!invoice.qonto_id) {
    throw new Error('PDF non disponible — cette facture n\'est pas synchronisée avec Qonto.')
  }
  const detail = await qontoApiCall<{ client_invoice: { attachment_id?: string } }>(
    `/client_invoices/${invoice.qonto_id}`,
  )
  const attachmentId = detail?.client_invoice?.attachment_id
  if (!attachmentId) {
    throw new Error('PDF Qonto en cours de génération, réessayez dans quelques instants.')
  }
  return downloadQontoAttachment(attachmentId)
}

export async function downloadInvoicePDF(invoice: Invoice): Promise<void> {
  const blob = await fetchQontoInvoicePDF(invoice)
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = `${invoice.numero}.pdf`
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

export async function previewInvoicePDF(invoice: Invoice): Promise<string> {
  const blob = await fetchQontoInvoicePDF(invoice)
  return URL.createObjectURL(blob)
}

// ============ QUOTE PDF GENERATION ============

export async function generateQuotePDF(devis: Devis): Promise<Blob> {
  const doc = new jsPDF()
  const pageWidth = doc.internal.pageSize.getWidth()

  // Colors
  const primaryColor: [number, number, number] = [76, 175, 80] // Mint green
  const textColor: [number, number, number] = [66, 50, 43] // Brown

  // ============ HEADER ============

  // Logo placeholder
  doc.setFillColor(...primaryColor)
  doc.rect(15, 10, 40, 15, 'F')
  doc.setTextColor(255, 255, 255)
  doc.setFontSize(12)
  doc.setFont('helvetica', 'bold')
  doc.text(COMPANY_INFO.name, 20, 20)

  // Company info (left side)
  doc.setTextColor(...textColor)
  doc.setFontSize(9)
  doc.setFont('helvetica', 'normal')
  doc.text(COMPANY_INFO.name, 15, 35)
  doc.text(`SIRET: ${COMPANY_INFO.siret}`, 15, 40)
  doc.text(COMPANY_INFO.address, 15, 45)
  doc.text(COMPANY_INFO.city, 15, 50)
  doc.text(COMPANY_INFO.email, 15, 55)
  doc.text(COMPANY_INFO.phone, 15, 60)

  // Client info (right side)
  doc.setFont('helvetica', 'bold')
  doc.text('Devis pour:', 120, 35)
  doc.setFont('helvetica', 'normal')

  const contactName = devis.contact
    ? `${devis.contact.prenom || ''} ${devis.contact.nom || ''}`.trim()
    : 'Client'
  doc.text(contactName, 120, 42)

  if (devis.contact?.entreprise) {
    doc.text(devis.contact.entreprise, 120, 48)
  }
  if (devis.contact?.email) {
    doc.text(devis.contact.email, 120, devis.contact?.entreprise ? 54 : 48)
  }

  // ============ QUOTE TITLE ============

  doc.setFontSize(22)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(...primaryColor)
  doc.text('DEVIS', 15, 80)

  doc.setFontSize(14)
  doc.setTextColor(...textColor)
  doc.text(devis.numero, 50, 80)

  // Quote details
  doc.setFontSize(10)
  doc.setFont('helvetica', 'normal')
  doc.text(`Date d'emission: ${formatDate(devis.date_emission)}`, 15, 90)
  doc.text(`Valable jusqu'au: ${formatDate(devis.date_validite)}`, 15, 96)

  if (devis.titre) {
    doc.setFont('helvetica', 'bold')
    doc.text(`Objet: ${devis.titre}`, 15, 102)
    doc.setFont('helvetica', 'normal')
  }

  // Description
  let startY = devis.titre ? 110 : 105
  if (devis.description) {
    doc.setFontSize(9)
    doc.text(devis.description, 15, startY, { maxWidth: pageWidth - 30 })
    startY += 10
  }

  // ============ LINE ITEMS TABLE ============

  const lines = (devis.lignes || []) as LigneDevis[]
  const tableData = lines.map((line) => [
    line.description,
    line.quantite.toString(),
    formatCurrency(line.prix_unitaire),
    `${line.taux_tva}%`,
    formatCurrency(line.total_ht),
  ])

  autoTable(doc, {
    startY,
    head: [['Description', 'Qte', 'Prix unitaire HT', 'TVA', 'Total HT']],
    body: tableData,
    theme: 'striped',
    headStyles: {
      fillColor: primaryColor,
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: 9,
    },
    bodyStyles: {
      textColor: textColor,
      fontSize: 9,
    },
    columnStyles: {
      0: { cellWidth: 80 },
      1: { cellWidth: 20, halign: 'center' },
      2: { cellWidth: 30, halign: 'right' },
      3: { cellWidth: 20, halign: 'center' },
      4: { cellWidth: 30, halign: 'right' },
    },
    alternateRowStyles: {
      fillColor: [245, 245, 240],
    },
    margin: { left: 15, right: 15 },
  })

  // Get the final Y position after the table
  const finalY = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 10

  // ============ TOTALS ============

  const totalsX = pageWidth - 75

  doc.setFontSize(10)
  doc.setFont('helvetica', 'normal')
  doc.setTextColor(...textColor)

  let currentY = finalY

  // Sous-total HT
  doc.text('Sous-total HT:', totalsX, currentY)
  doc.text(formatCurrency(devis.montant_ht || 0), pageWidth - 15, currentY, { align: 'right' })
  currentY += 7

  // TVA
  doc.text(`TVA ${devis.taux_tva}%:`, totalsX, currentY)
  doc.text(formatCurrency(devis.montant_tva || 0), pageWidth - 15, currentY, { align: 'right' })
  currentY += 7

  // Line separator
  doc.setDrawColor(...primaryColor)
  doc.setLineWidth(0.5)
  doc.line(totalsX - 5, currentY - 2, pageWidth - 15, currentY - 2)

  // Total TTC
  doc.setFontSize(12)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(...primaryColor)
  doc.text('Total TTC:', totalsX, currentY + 5)
  doc.text(formatCurrency(devis.montant_ttc || 0), pageWidth - 15, currentY + 5, { align: 'right' })

  // ============ CONDITIONS ============

  if (devis.conditions) {
    currentY += 25
    doc.setFontSize(9)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(...textColor)
    doc.text('Conditions:', 15, currentY)
    doc.setFont('helvetica', 'normal')
    const conditionsLines = doc.splitTextToSize(devis.conditions, pageWidth - 30)
    doc.text(conditionsLines, 15, currentY + 6)
    currentY += 6 + (conditionsLines.length * 4)
  }

  // ============ SIGNATURE ============

  if (devis.signature_client && devis.status === 'accepted') {
    currentY += 15

    // Signature section
    doc.setFontSize(10)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(...textColor)
    doc.text('Signature du client:', 15, currentY)

    currentY += 5

    // Check if signature is an image (data URL) or typed text
    if (devis.signature_client.startsWith('data:image')) {
      // Canvas signature - add the image
      try {
        doc.addImage(devis.signature_client, 'PNG', 15, currentY, 60, 20)
        currentY += 25
      } catch (error) {
        console.error('Error adding signature image:', error)
        doc.setFont('helvetica', 'italic')
        doc.setFontSize(9)
        doc.text('[Signature numerique]', 15, currentY + 10)
        currentY += 15
      }
    } else {
      // Typed signature
      doc.setFont('helvetica', 'italic')
      doc.setFontSize(12)
      doc.text(devis.signature_client, 15, currentY + 10)
      currentY += 15
    }

    // Signature date and IP
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(8)
    doc.setTextColor(128, 128, 128)
    if (devis.signature_date) {
      doc.text(`Signe le: ${formatDate(devis.signature_date)} ${new Date(devis.signature_date).toLocaleTimeString('fr-FR')}`, 15, currentY)
    }
    if (devis.ip_signature) {
      doc.text(`IP: ${devis.ip_signature}`, 15, currentY + 4)
    }
  }

  // ============ LEGAL MENTIONS ============

  doc.setFontSize(8)
  doc.setFont('helvetica', 'normal')
  doc.setTextColor(128, 128, 128)

  const footerY = 275
  doc.text('Mentions legales:', 15, footerY)
  doc.text(`- Ce devis est valable ${devis.validite_jours || 30} jours a compter de la date d'emission.`, 15, footerY + 5)
  doc.text('- TVA non applicable, art. 293 B du CGI (ou TVA applicable selon le regime).', 15, footerY + 10)

  // Page number
  doc.setFontSize(9)
  doc.setTextColor(...textColor)
  doc.text('Page 1/1', pageWidth / 2, 290, { align: 'center' })

  return doc.output('blob')
}

export async function downloadQuotePDF(devis: Devis): Promise<void> {
  const blob = await generateQuotePDF(devis)
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = `Devis_${devis.numero}.pdf`
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

export async function previewQuotePDF(devis: Devis): Promise<string> {
  const blob = await generateQuotePDF(devis)
  return URL.createObjectURL(blob)
}
